//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { useSelector } from 'react-redux';

import { Reel } from '@apis/reels/reelsApiModel';
import {
  FilterOption,
  NestedFilterOption,
} from '@components/media-filters/filterSectionModels';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';

export type DistributionFilterOption = NestedFilterOption<string>;

export const getIsSelected = (
  options: FilterOption<string>[],
  value: string,
): boolean => {
  return options.find((option) => option.value === value)?.isSelected ?? false;
};

export const useDistributionOptions = () => {
  const { client } = useSelector((state) => state.session);
  const isLike2BuyEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.Like2Buy),
  );
  const isSchedulerEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.InstagramScheduler),
  );
  const isRevealEnabled =
    useSelector(createHasFeatureSelector(FeatureNames.RevealPage)) ||
    client.isSyndication;
  const isTributaryEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.TributarySelfServe),
  );

  const getOptions = (
    options: FilterOption<string>[],
    reels: Reel[],
  ): DistributionFilterOption[] => {
    return [
      {
        isEnabled: isSchedulerEnabled,
        isSelected: false,
        label: 'Scheduler',
        subOptions: [
          {
            isSelected: getIsSelected(options, 's:instagram'),
            label: 'Instagram',
            value: 's:instagram',
          },
          {
            isSelected: getIsSelected(options, 's:pinterest'),
            label: 'Pinterest',
            value: 's:pinterest',
          },
          {
            isSelected: getIsSelected(options, 's:youtube'),
            label: 'YouTube',
            value: 's:youtube',
          },
        ],
        value: 'subOptions',
      },
      {
        isEnabled: isLike2BuyEnabled,
        isSelected: getIsSelected(options, 'like2buy'),
        label: 'Like2Buy',
        value: 'like2buy',
      },
      {
        isEnabled: reels.length > 0,
        isSelected: false,
        label: 'Galleries',
        subOptions: reels.map((reel) => {
          // ContentLibraryApi identify reels by the "f:" prefix
          const id = `f:${reel.id}`;

          return {
            isSelected: getIsSelected(options, id),
            label: reel.name,
            value: id,
          };
        }),
        value: 'subOptions',
      },
      {
        isEnabled: isRevealEnabled,
        isSelected: getIsSelected(options, 'reveal'),
        label: 'Reveal',
        value: 'reveal',
      },
      {
        isEnabled: isRevealEnabled,
        isSelected: getIsSelected(options, 'revolve'),
        label: 'Revolve',
        value: 'revolve',
      },
      {
        isEnabled: isTributaryEnabled,
        isSelected: getIsSelected(options, 'showroom'),
        label: 'Showroom',
        value: 'showroom',
      },
    ];
  };

  return getOptions;
};
